import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
  Upload,
  Radio,
  Descriptions,
  Divider,
  Row,
  Col,
  Empty,
  DatePicker,
} from "antd";
import dayjs from "dayjs";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  PrinterOutlined,
  TeamOutlined,
  EnvironmentOutlined,
  CloudUploadOutlined,
} from "@ant-design/icons";
import adminServices, { adminRestaurantPosService } from "../../services/adminService";
import { formatLkr } from "../../utils/currency";
import {
  MenuItemCard,
  MenuItemDetailsDialog,
} from "./components/MenuItemComponents";
import { CreateItemForm } from "./components/CreateItemFrom";
import { EditWebsiteContentForm } from "./components/EditWebsiteContentForm";
import { CreatePosOrderForm } from "./components/CreatePosOrderForm";
import { OrderDetailsModal } from "./components/OrderDetailsModal";
import { TaxInvoiceModal } from "./components/TaxInvoiceModal";
import { KitchenGroupManagement, MenuItemFormModal } from "./components";
import type { KitchenGroup } from "./components";
import { printJobService } from "../../services/printerService";
import { PAYMENT_METHOD_OPTIONS } from "../../constants/paymentMethods";
import { canViewPrices, hasPermission } from '../../utils/permissions';

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api/v1";
const getToken = () => localStorage.getItem("admin_token") || "";

const { Text } = Typography;
const { Dragger } = Upload;

const ORDER_STATUS_META: Record<string, { label: string; color: string }> = {
  kot_pending: { label: "KOT Pending", color: "orange" },
  preparing: { label: "Preparing", color: "blue" },
  served: { label: "Served", color: "green" },
  billed: { label: "Billed", color: "purple" },
  paid: { label: "Paid", color: "cyan" },
  cancelled: { label: "Cancelled", color: "red" },
};


interface DiningTable {
  id: string;
  table_number: string;
  capacity: number;
  location?: string;
  status: "available" | "occupied" | "reserved" | "cleaning" | "out_of_service";
}

interface MenuCategory {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  display_order: number;
}

export interface MenuItem {
  id: string;
  category_id?: string;
  category_name?: string;
  name: string;
  description?: string;
  price_take_away: number;
  price_dine_in: number;
  is_available: boolean;
  is_active: boolean;
  image_url?: string;
  badge?: string;
  is_featured: boolean;
  display_order: number;
  kitchen_printer_group_id?: string;
  kitchen_group_name?: string;
}

export interface RestaurantPageContent {
  menu_hero_eyebrow: string;
  menu_hero_title: string;
  menu_hero_subtitle: string;
  menu_hero_image: string;
  menu_feature_image: string;
  menu_reservation_kicker: string;
  menu_reservation_title: string;
  menu_reservation_description: string;
  restaurant_hero_eyebrow: string;
  restaurant_hero_title: string;
  restaurant_hero_subtitle: string;
  restaurant_hero_image: string;
  restaurant_intro_kicker: string;
  restaurant_intro_title: string;
  restaurant_intro_paragraph_one: string;
  restaurant_intro_paragraph_two: string;
  featured_section_kicker: string;
  featured_section_title: string;
  experiences_section_kicker: string;
  experiences_section_title: string;
  cta_title: string;
  cta_description: string;
  cta_button_text: string;
  cta_button_link: string;
  opening_hours: Array<{ meal: string; hours: string }>;
  dining_experiences: Array<{
    name: string;
    description: string;
    icon: string;
  }>;
}

interface PosOrder {
  id: string;
  order_number: string;
  order_type: "dine_in" | "room_service" | "take_away";
  table_id?: string;
  table_number?: string;
  guest_name?: string;
  booking_id?: string;
  status:
    | "kot_pending"
    | "preparing"
    | "served"
    | "billed"
    | "paid"
    | "cancelled";
  total_amount: number;
  created_at: string;
}

interface KotQueueItem {
  order_id: string;
  order_number: string;
  order_type?: string;
  order_status?: string;
  created_at?: string;
  order_item_id: string;
  table_number?: string;
  kitchen_group_name?: string;
  menu_item_name: string;
  quantity: number;
  note?: string;
  kot_status: "pending" | "preparing" | "ready" | "served";
}

/** All KOT items of a single order — one row in the grouped queue. */
interface KotOrderGroup {
  order_id: string;
  order_number: string;
  order_type?: string;
  order_status?: string;
  created_at?: string;
  table_number?: string;
  items: KotQueueItem[];
}


const RestaurantPOS: React.FC = () => {
  const canManage = hasPermission("restaurant:edit");
  // Creating/editing orders and marking them paid (backend: restaurant:orders).
  const canTakeOrders = hasPermission("restaurant:orders");
  // Advancing an order through prep states (backend also allows kitchen:edit).
  const canAdvanceStatus = hasPermission("restaurant:orders") || hasPermission("kitchen:edit");
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  // Every tab key must be allowed, otherwise clicking a tab writes ?tab=…
  // but activeKey snaps back to the fallback and the tab never switches.
  const activeTab = [
    "tables",
    "menu",
    "website-content",
    "orders",
    "kot",
  ].includes(tabParam || "")
    ? (tabParam as string)
    : "tables";

  const [tables, setTables] = useState<DiningTable[]>([]);
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [kitchenGroups, setKitchenGroups] = useState<KitchenGroup[]>([]);
  const [orders, setOrders] = useState<PosOrder[]>([]);
  const [kotQueue, setKotQueue] = useState<KotQueueItem[]>([]);
  const [checkedInBookings, setCheckedInBookings] = useState<any[]>([]);

  const [tableModalVisible, setTableModalVisible] = useState(false);
  const [editingTable, setEditingTable] = useState<DiningTable | null>(null);
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [itemModalVisible, setItemModalVisible] = useState(false);
  const [contentModalVisible, setContentModalVisible] = useState(false);
  const [orderModalVisible, setOrderModalVisible] = useState(false);
  const [orderDetailsVisible, setOrderDetailsVisible] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [editingOrder, setEditingOrder] = useState<any>(null);
  const [taxInvoiceVisible, setTaxInvoiceVisible] = useState(false);
  const [taxInvoiceOrderId, setTaxInvoiceOrderId] = useState<string | null>(null);
  const [taxInvoiceBookingId, setTaxInvoiceBookingId] = useState<string | null>(null);
  const [taxInvoiceGuestName, setTaxInvoiceGuestName] = useState<string>("");
  const [paidStatusOrderId, setPaidStatusOrderId] = useState<string | null>(null);
  const [paidStatusMethod, setPaidStatusMethod] = useState<string>("cash");

  const [menuSearchQuery, setMenuSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [menuFilterCategory, setMenuFilterCategory] = useState<string | "all">("all");
  const [menuFilterAvailability, setMenuFilterAvailability] = useState<"all" | "active" | "inactive">("all");

  const [orderSearchQuery, setOrderSearchQuery] = useState("");
  const [debouncedOrderSearchQuery, setDebouncedOrderSearchQuery] = useState("");
  const [orderFilterType, setOrderFilterType] = useState<string | "all">("all");
  const [orderDate, setOrderDate] = useState<dayjs.Dayjs | null>(dayjs());

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedOrderSearchQuery(orderSearchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [orderSearchQuery]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(menuSearchQuery);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [menuSearchQuery]);

  const filteredMenuItems = useMemo(() => {
    return menuItems.filter((item) => {
      const matchesSearch =
        item.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
        (item.description &&
          item.description
            .toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase()));
      const matchesCategory =
        menuFilterCategory === "all" || item.category_id === menuFilterCategory;
      const matchesAvailability =
        menuFilterAvailability === "all" ||
        (menuFilterAvailability === "active"
          ? item.is_active
          : !item.is_active);
      return matchesSearch && matchesCategory && matchesAvailability;
    });
  }, [
    menuItems,
    debouncedSearchQuery,
    menuFilterCategory,
    menuFilterAvailability,
  ]);

  const filteredOrders = useMemo(() => {
    let result = orders.filter((order: any) => {
      const q = debouncedOrderSearchQuery.toLowerCase();
      const matchesSearch =
        order.order_number?.toLowerCase().includes(q) ||
        order.guest_name?.toLowerCase().includes(q) ||
        order.table_number?.toLowerCase().includes(q) ||
        order.room_number?.toLowerCase().includes(q);
      const matchesType = orderFilterType === "all" || order.order_type === orderFilterType;
      
      return matchesSearch && matchesType;
    });

    result.sort((a, b) => {
      const aInactive = ['paid', 'cancelled'].includes(a.status);
      const bInactive = ['paid', 'cancelled'].includes(b.status);
      if (aInactive && !bInactive) return 1;
      if (!aInactive && bInactive) return -1;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    return result;
  }, [orders, debouncedOrderSearchQuery, orderFilterType]);

  const [editingCategory, setEditingCategory] = useState<MenuCategory | null>(
    null,
  );
  const [editingMenuItem, setEditingMenuItem] = useState<MenuItem | null>(null);
  const [viewingMenuItem, setViewingMenuItem] = useState<MenuItem | null>(null);
  const [websiteContent, setWebsiteContent] =
    useState<RestaurantPageContent | null>(null);

  const [tableForm] = Form.useForm();
  const [categoryForm] = Form.useForm();

  useEffect(() => {
    const initialize = async () => {
      try {
        setLoading(true);
        await Promise.all([
          refreshTables(),
          refreshMenu(),
          refreshKot(),
          refreshWebsiteContent(),
          refreshBookings(),
        ]);
      } catch (error: any) {
        console.error("Error initializing POS data:", error);
        message.error(error?.message || "Failed to load POS data");
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, []);

  const refreshTables = async () => {
    const response = await adminRestaurantPosService.getDiningTables();
    setTables(
      Array.isArray(response.data) ? (response.data as DiningTable[]) : [],
    );
  };

  const refreshBookings = async () => {
    try {
      const response = await adminServices.frontDeskService.getBookings({ status: 'checked_in' });
      setCheckedInBookings(Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      console.error("Error fetching checked in bookings:", error);
    }
  };

  const refreshMenu = async () => {
    const [catRes, itemRes, kgRes] = await Promise.all([
      adminRestaurantPosService.getMenuCategories(),
      adminRestaurantPosService.getMenuItems(),
      adminRestaurantPosService.getKitchenGroups(),
    ]);
    setCategories(
      Array.isArray(catRes.data) ? (catRes.data as MenuCategory[]) : [],
    );
    setMenuItems(
      Array.isArray(itemRes.data) ? (itemRes.data as MenuItem[]) : [],
    );
    setKitchenGroups(
      Array.isArray(kgRes.data) ? (kgRes.data as KitchenGroup[]) : [],
    );
  };

  const refreshOrders = async () => {
    const hasSearch = debouncedOrderSearchQuery.length > 0;
    const response = await adminRestaurantPosService.getOrders({
      startDate: (!hasSearch && orderDate) ? orderDate.startOf('day').toISOString() : undefined,
      endDate: (!hasSearch && orderDate) ? orderDate.endOf('day').toISOString() : undefined,
    });
    setOrders(
      Array.isArray(response.data) ? (response.data as PosOrder[]) : [],
    );
  };

  useEffect(() => {
    refreshOrders();
  }, [orderDate, debouncedOrderSearchQuery]); // eslint-disable-line

  const refreshKot = async () => {
    const response = await adminRestaurantPosService.getKotQueue();
    setKotQueue(
      Array.isArray(response.data) ? (response.data as KotQueueItem[]) : [],
    );
  };

  const refreshWebsiteContent = async () => {
    const response = await adminRestaurantPosService.getRestaurantPageContent();
    if (response.data) {
      setWebsiteContent(response.data as RestaurantPageContent);
    }
  };

  const tableOptions = useMemo(
    () =>
      tables.map((table) => ({
        label: `${table.table_number} (${table.status})`,
        value: table.id,
      })),
    [tables],
  );

  const menuItemOptions = useMemo(
    () =>
      menuItems
        .filter((item) => item.is_available)
        .map((item) => ({
          label: canViewPrices() ? `${item.name} - ${formatLkr(item.price_dine_in)}` : item.name,
          value: item.id,
        })),
    [menuItems],
  );

  const createTable = async (values: any) => {
    try {
      if (editingTable) {
        await adminRestaurantPosService.updateDiningTable(
          editingTable.id,
          values,
        );
        message.success("Dining table updated");
      } else {
        await adminRestaurantPosService.createDiningTable(values);
        message.success("Dining table created");
      }
      setTableModalVisible(false);
      setEditingTable(null);
      tableForm.resetFields();
      refreshTables();
    } catch (error: any) {
      console.error("Error creating table:", error);
      message.error(error?.message || "Failed to create table");
    }
  };

  const deleteTable = async (id: string) => {
    try {
      await adminRestaurantPosService.deleteDiningTable(id);
      message.success("Dining table deleted");
      refreshTables();
    } catch (error: any) {
      console.error("Error deleting table:", error);
      message.error(error?.message || "Failed to delete table");
    }
  };

  const createCategory = async (values: any) => {
    try {
      if (editingCategory) {
        await adminRestaurantPosService.updateMenuCategory(
          editingCategory.id,
          values,
        );
        message.success("Menu category updated");
      } else {
        await adminRestaurantPosService.createMenuCategory(values);
        message.success("Menu category created");
      }
      setCategoryModalVisible(false);
      setEditingCategory(null);
      categoryForm.resetFields();
      refreshMenu();
    } catch (error: any) {
      console.error("Error creating category:", error);
      message.error(error?.message || "Failed to save category");
    }
  };

  const createMenuItem = async (values: any, selectedImage: File | null) => {
    try {
      setLoading(true);
      let finalImageUrl = values.image_url || "";

      if (selectedImage) {
        const uploadRes =
          await adminRestaurantPosService.uploadMenuImage(selectedImage);
        finalImageUrl = String(
          (uploadRes.data as { image_url?: string })?.image_url || "",
        );
      }

      const payload = { ...values, image_url: finalImageUrl };

      if (editingMenuItem) {
        await adminRestaurantPosService.updateMenuItem(
          editingMenuItem.id,
          payload,
        );
        message.success("Menu item updated");
      } else {
        await adminRestaurantPosService.createMenuItem(payload);
        message.success("Menu item created");
      }

      setItemModalVisible(false);
      setEditingMenuItem(null);
      refreshMenu();
    } catch (error: any) {
      console.error("Error saving menu item:", error);
      message.error(error?.message || "Failed to save menu item");
    } finally {
      setLoading(false);
    }
  };

  const deleteCategory = async (id: string) => {
    try {
      await adminRestaurantPosService.deleteMenuCategory(id);
      message.success("Menu category deactivated");
      refreshMenu();
    } catch (error: any) {
      console.error("Error deactivating category:", error);
      message.error(error?.message || "Failed to deactivate category");
    }
  };

  const deleteMenuItem = async (id: string) => {
    try {
      await adminRestaurantPosService.deleteMenuItem(id);
      message.success("Menu item deactivated");
      refreshMenu();
    } catch (error: any) {
      console.error("Error deactivating menu item:", error);
      message.error(error?.message || "Failed to deactivate menu item");
    }
  };

  const saveWebsiteContent = async (
    values: RestaurantPageContent,
    files: {
      menu_hero_image?: File;
      menu_feature_image?: File;
      restaurant_hero_image?: File;
    },
  ) => {
    try {
      setLoading(true);

      const payload = { ...values };

      if (files.menu_hero_image) {
        const uploadRes = await adminRestaurantPosService.uploadMenuImage(
          files.menu_hero_image,
        );
        payload.menu_hero_image = String(
          (uploadRes.data as any)?.image_url || "",
        );
      }
      if (files.menu_feature_image) {
        const uploadRes = await adminRestaurantPosService.uploadMenuImage(
          files.menu_feature_image,
        );
        payload.menu_feature_image = String(
          (uploadRes.data as any)?.image_url || "",
        );
      }
      if (files.restaurant_hero_image) {
        const uploadRes = await adminRestaurantPosService.uploadMenuImage(
          files.restaurant_hero_image,
        );
        payload.restaurant_hero_image = String(
          (uploadRes.data as any)?.image_url || "",
        );
      }

      await adminRestaurantPosService.updateRestaurantPageContent(payload);
      message.success("Restaurant website content updated");
      setContentModalVisible(false);
      refreshWebsiteContent();
    } catch (error: any) {
      console.error("Error updating restaurant website content:", error);
      message.error(error?.message || "Failed to update website content");
    } finally {
      setLoading(false);
    }
  };

  const createOrder = async (values: any) => {
    try {
      const payload = {
        order_type: values.order_type,
        table_id: values.table_ids && values.table_ids.length > 0 ? values.table_ids[0] : values.table_id,
        table_ids: values.table_ids || (values.table_id ? [values.table_id] : undefined),
        booking_id: values.booking_id,
        room_id: values.room_id,
        guest_name: values.guest_name,
        promotion_code: values.promotion_code,
        discount_amount: values.discount_amount || 0,
        discount_type: values.discount_type || 'none',
        discount_value: values.discount_value || 0,
        service_charge: values.service_charge || 0,
        service_charge_type: values.service_charge_type || 'none',
        service_charge_value: values.service_charge_value || 0,
        tax_amount: values.tax_amount || 0,
        tax_type: values.tax_type || 'none',
        tax_value: values.tax_value || 0,
        items: (values.items || []).map((item: any) => ({
          menu_item_id: item.menu_item_id,
          quantity: item.quantity,
          note: item.note,
          no_of_guests: item.no_of_guests,
          no_of_days: item.no_of_days,
        })),
      };

      if (editingOrder) {
        await adminRestaurantPosService.updateOrder(editingOrder.id, payload);
        message.success("POS order updated");
      } else {
        await adminRestaurantPosService.createOrder(payload);
        message.success("POS order created");
      }

      setOrderModalVisible(false);
      setEditingOrder(null);
      refreshOrders();
      refreshKot();
      refreshTables();
    } catch (error: any) {
      console.error("Error saving order:", error);
      message.error(error?.message || "Failed to save POS order");
    }
  };

  const updateOrderStatus = async (
    orderId: string,
    status: PosOrder["status"],
    paymentMethod?: string,
  ) => {
    try {
      await adminRestaurantPosService.updateOrderStatus(orderId, status, paymentMethod);
      message.success("Order status updated");
      refreshOrders();
      // the order status drives the KOT items — refresh the kitchen queue too
      refreshKot();
    } catch (error: any) {
      console.error("Error updating order status:", error);
      message.error(error?.message || "Failed to update order status");
    }
  };

  const handleStatusChange = (
    orderId: string,
    nextStatus: PosOrder["status"],
  ) => {
    if (nextStatus === "paid") {
      setPaidStatusOrderId(orderId);
      setPaidStatusMethod("cash");
      return;
    }
    updateOrderStatus(orderId, nextStatus);
  };

  const confirmPaidStatus = async () => {
    if (!paidStatusOrderId) return;
    const orderId = paidStatusOrderId;
    setPaidStatusOrderId(null);
    await updateOrderStatus(orderId, "paid", paidStatusMethod);
  };

  const updateKotStatus = async (
    orderItemId: string,
    status: KotQueueItem["kot_status"],
  ) => {
    try {
      await adminRestaurantPosService.updateKotItemStatus(orderItemId, status);
      message.success("KOT status updated");
      refreshKot();
      // KOT item changes can move the parent order status — refresh orders too
      refreshOrders();
    } catch (error: any) {
      console.error("Error updating KOT status:", error);
      message.error(error?.message || "Failed to update KOT status");
    }
  };

  const printReceipt = async (orderId: string) => {
    try {
      await printJobService.submit({
        idempotency_key: `receipt-${orderId}-${Date.now()}`,
        printer_role: 'RECEIPT',
        content_type: 'receipt',
        order_id: orderId,
      });
      message.success('Receipt sent to receipt printer');
    } catch (error: any) {
      console.error(error);
      message.error(error?.message || 'Failed to send receipt to printer');
    }
  };

  const printKot = async (orderId: string) => {
    try {
      await printJobService.submit({
        idempotency_key: `kot-${orderId}-${Date.now()}`,
        printer_role: 'KITCHEN',
        content_type: 'kot',
        order_id: orderId,
      });
      message.success('KOT sent to kitchen printer');
    } catch (error: any) {
      console.error(error);
      message.error(error?.message || 'Failed to send KOT to printer');
    }
  };

  const openReceiptPdf = async (orderId: string) => {
    try {
      const response = await fetch(`${API_BASE}/restaurant-pos/orders/${orderId}/receipt/pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!response.ok) throw new Error("Failed to fetch receipt PDF");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const newWindow = window.open(url, "_blank");
      if (newWindow) newWindow.focus();
    } catch (error: any) {
      console.error(error);
      message.error(error?.message || "Failed to load receipt PDF");
    }
  };

  const openTaxInvoiceModal = (orderId: string, bookingId?: string, guestName?: string) => {
    setTaxInvoiceOrderId(orderId);
    setTaxInvoiceBookingId(bookingId || null);
    setTaxInvoiceGuestName(guestName || "");
    setTaxInvoiceVisible(true);
  };

  const printTaxInvoice = async (orderId: string, customerDetails: {
    customerName: string;
    companyName?: string;
    phone?: string;
    address?: string;
    tinVatNumber?: string;
  }) => {
    try {
      await printJobService.submit({
        idempotency_key: `tax-${orderId}-${Date.now()}`,
        printer_role: 'RECEIPT',
        content_type: 'tax_invoice',
        order_id: orderId,
        customer_details: customerDetails,
      });
      message.success('Tax invoice sent to receipt printer');
    } catch (error: any) {
      console.error(error);
      message.error(error?.message || 'Failed to send tax invoice to printer');
    }
  };

  const categoryColumns = [
    { title: "Category", dataIndex: "name", key: "name" },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (value: string) => value || "-",
    },
    {
      title: "Display Order",
      dataIndex: "display_order",
      key: "display_order",
    },
    {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      render: (active: boolean) => (
        <Tag color={active ? "green" : "red"}>{active ? "Yes" : "No"}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: MenuCategory) =>
        canManage ? (
          <Space>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingCategory(record);
                categoryForm.setFieldsValue(record);
                setCategoryModalVisible(true);
              }}
            >
              Edit
            </Button>
            <Popconfirm
              title="Deactivate this category?"
              description="Items will remain but become uncategorized."
              onConfirm={() => deleteCategory(record.id)}
            >
              <Button size="small" danger icon={<DeleteOutlined />}>
                Deactivate
              </Button>
            </Popconfirm>
          </Space>
        ) : null,
    },
  ];

  const orderColumns = [
    { 
      title: "Order Info", 
      key: "order_info",
      render: (_: any, record: any) => {
        const date = new Date(record.created_at);
        
        const isOngoing = !['paid', 'cancelled'].includes(record.status);

        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Text strong>{record.order_number}</Text>
              {isOngoing && <div className="pulse-indicator" title="Ongoing Order" />}
            </div>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Text>
          </div>
        );
      }
    },
    { 
      title: "Order Type", 
      dataIndex: "order_type", 
      key: "order_type",
      render: (type: string) => {
        if (type === "room_service") return <Tag color="blue">Room Service</Tag>;
        if (type === "take_away") return <Tag color="green">Take Away</Tag>;
        return <Tag color="orange">Dine In</Tag>;
      }
    },
    {
      title: "Guest & Location",
      key: "guest_location",
      render: (_: any, record: any) => {
        const isRoomService = record.order_type === "room_service";
        const location = isRoomService ? `Room: ${record.room_number || "-"}` : `Table: ${record.table_number || "-"}`;
        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Text>{record.guest_name || "Walk-in Guest"}</Text>
            <Text type="secondary" style={{ fontSize: "12px" }}>{location}</Text>
          </div>
        );
      }
    },
    {
      title: "Duration",
      key: "duration",
      render: (_: any, record: any) => {
        const isRoomService = record.order_type === "room_service";
        const maxDays = Number(record.max_days) || 1;
        if (!isRoomService || maxDays <= 1) return <Text type="secondary">-</Text>;

        const startDate = new Date(record.created_at);
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + maxDays - 1);
        
        const now = new Date();
        const startOfDay = new Date(startDate);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);
        const isOngoing = now >= startOfDay && now <= endOfDay;

        return (
          <div style={{ 
            padding: "4px 8px", 
            backgroundColor: isOngoing ? "#f6ffed" : "rgba(0,0,0,0.02)", 
            border: isOngoing ? "1px solid #b7eb8f" : "1px solid transparent",
            borderRadius: "6px", 
            fontSize: "12px" 
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
              <Text type="secondary" style={{ fontSize: "12px" }}>Days:</Text>
              <Text strong style={{ fontSize: "12px" }}>{maxDays}</Text>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Text type="secondary" style={{ fontSize: "12px" }}>Until:</Text>
              <Text style={{ fontSize: "12px" }}>{endDate.toLocaleDateString()}</Text>
            </div>
          </div>
        );
      }
    },
    {
      title: "Total",
      dataIndex: "total_amount",
      key: "total_amount",
      render: (value: number) =>
        canViewPrices() ? (
          <Text strong style={{ color: "#B79A68" }}>{formatLkr(Number(value))}</Text>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "Payment",
      key: "payment_method",
      render: (_: any, record: any) =>
        record.payment_method ? (
          <Tag color="blue">{String(record.payment_method).replace("_", " ").toUpperCase()}</Tag>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "Status & Actions",
      key: "actions",
      render: (_: any, record: any) => (
        <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {canAdvanceStatus ? (
            <Select
              size="small"
              style={{ width: 120 }}
              value={record.status}
              onChange={(nextStatus) => handleStatusChange(record.id, nextStatus)}
              options={[
                { label: "KOT Pending", value: "kot_pending" },
                { label: "Preparing", value: "preparing" },
                { label: "Served", value: "served" },
                { label: "Billed", value: "billed" },
                // Marking paid is a money action — restaurant:orders only.
                ...(canTakeOrders ? [{ label: "Paid", value: "paid" }] : []),
                { label: "Cancelled", value: "cancelled" },
              ]}
            />
          ) : (
            <Tag>{String(record.status || "").replace("_", " ").toUpperCase()}</Tag>
          )}
          {canViewPrices() && (
            <Popconfirm
              title="Print Receipt?"
              description="This will send the receipt to the printer."
              onConfirm={(e) => {
                e?.stopPropagation();
                printReceipt(record.id);
              }}
              onCancel={(e) => e?.stopPropagation()}
              okText="Print"
            >
              <Button
                size="small"
                type="default"
                onClick={(e) => e.stopPropagation()}
              >
                🖨️
              </Button>
            </Popconfirm>
          )}
          {canViewPrices() && (
            <Button
              size="small"
              type="default"
              onClick={(e) => {
                e.stopPropagation();
                openTaxInvoiceModal(record.id, record.booking_id, record.guest_name);
              }}
            >
              🧾
            </Button>
          )}
          <Popconfirm
            title="Print KOT?"
            description="This will send the KOT to the kitchen printer."
            onConfirm={(e) => {
              e?.stopPropagation();
              printKot(record.id);
            }}
            onCancel={(e) => e?.stopPropagation()}
            okText="Print"
          >
            <Button
              size="small"
              type="default"
              onClick={(e) => e.stopPropagation()}
            >
              📋
            </Button>
          </Popconfirm>
          {canViewPrices() && (
            <Popconfirm
              title="Print A4 Receipt?"
              description="This will open the receipt in a new tab."
              onConfirm={(e) => {
                e?.stopPropagation();
                openReceiptPdf(record.id);
              }}
              onCancel={(e) => e?.stopPropagation()}
            >
              <Button
                size="small"
                type="default"
                onClick={(e) => e.stopPropagation()}
              >
                📄
              </Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  // One row per order: bucket the queue (the API already returns the newest
  // order first) by order_id so a whole KOT reads as a single entry.
  const kotOrderGroups = useMemo<KotOrderGroup[]>(() => {
    const groups: KotOrderGroup[] = [];
    const byOrder = new Map<string, KotOrderGroup>();

    kotQueue.forEach((item) => {
      let group = byOrder.get(item.order_id);
      if (!group) {
        group = {
          order_id: item.order_id,
          order_number: item.order_number,
          order_type: item.order_type,
          order_status: item.order_status,
          created_at: item.created_at,
          table_number: item.table_number,
          items: [],
        };
        byOrder.set(item.order_id, group);
        groups.push(group);
      }
      group.items.push(item);
    });

    return groups;
  }, [kotQueue]);

  // Derived from the items themselves, using the same rule the backend uses to
  // keep the order status in step with its KOT items.
  const summarizeKotStatus = (items: KotQueueItem[]) => {
    const counts: Record<string, number> = {};
    items.forEach((item) => {
      counts[item.kot_status] = (counts[item.kot_status] || 0) + 1;
    });

    const statuses = items.map((item) => item.kot_status);
    const overall = statuses.every((status) => status === "served")
      ? "served"
      : statuses.some((status) => status !== "pending")
        ? "preparing"
        : "pending";

    const breakdown = ["pending", "preparing", "ready", "served"]
      .filter((status) => counts[status])
      .map((status) => `${counts[status]} ${status}`)
      .join(" · ");

    return { overall, breakdown };
  };

  const kotOrderColumns = [
    { title: "Order #", dataIndex: "order_number", key: "order_number" },
    {
      title: "Table",
      dataIndex: "table_number",
      key: "table_number",
      render: (value: string) => value || "-",
    },
    {
      title: "Items",
      key: "items",
      render: (_: any, record: KotOrderGroup) =>
        `${record.items.length} item${record.items.length === 1 ? "" : "s"}`,
    },
    {
      title: "KOT Status",
      key: "kot_status",
      render: (_: any, record: KotOrderGroup) => {
        const { overall, breakdown } = summarizeKotStatus(record.items);
        return (
          <Space size={4} wrap>
            <Tag
              color={
                overall === "pending"
                  ? "orange"
                  : overall === "served"
                    ? "green"
                    : "blue"
              }
            >
              {overall}
            </Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {breakdown}
            </Text>
          </Space>
        );
      },
    },
    {
      title: "Order Status",
      dataIndex: "order_status",
      key: "order_status",
      render: (status?: string) => {
        const meta = status ? ORDER_STATUS_META[status] : undefined;
        return meta ? <Tag color={meta.color}>{meta.label}</Tag> : status || "-";
      },
    },
    {
      title: "Time",
      dataIndex: "created_at",
      key: "created_at",
      render: (value?: string) => (value ? dayjs(value).format("HH:mm") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: KotOrderGroup) => (
        <Popconfirm
          title="Print KOT?"
          description="This will send the KOT to the kitchen printer."
          onConfirm={() => printKot(record.order_id)}
          okText="Print"
        >
          <Button size="small" icon={<PrinterOutlined />}>
            Print KOT
          </Button>
        </Popconfirm>
      ),
    },
  ];

  // The individual items live one level down, with the existing per-item
  // status control untouched.
  const kotItemColumns = [
    { title: "Item", dataIndex: "menu_item_name", key: "menu_item_name" },
    { title: "Qty", dataIndex: "quantity", key: "quantity" },
    {
      title: "Kitchen Group",
      dataIndex: "kitchen_group_name",
      key: "kitchen_group_name",
      render: (value: string) => value || "-",
    },
    {
      title: "Note",
      dataIndex: "note",
      key: "note",
      render: (value: string) => value || "-",
    },
    {
      title: "KOT Status",
      dataIndex: "kot_status",
      key: "kot_status",
      render: (status: KotQueueItem["kot_status"], record: KotQueueItem) => (
        <Select
          size="small"
          style={{ width: 130 }}
          value={status}
          onChange={(nextStatus) =>
            updateKotStatus(record.order_item_id, nextStatus)
          }
          options={[
            { label: "Pending", value: "pending" },
            { label: "Preparing", value: "preparing" },
            { label: "Ready", value: "ready" },
            { label: "Served", value: "served" },
          ]}
        />
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ marginBottom: 4 }}>Restaurant POS</h1>
        <Text type="secondary">
          Manage tables, menu, live KOT queue, billing and split payments.
        </Text>
      </div>

      <Spin spinning={loading}>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setSearchParams(key === "tables" ? {} : { tab: key })}
          items={[
            {
              key: "tables",
              label: "Tables",
              children: (
                <div>
                  <Space style={{ marginBottom: 16 }}>
                    {canManage && (
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                          setEditingTable(null);
                          tableForm.resetFields();
                          setTableModalVisible(true);
                        }}
                      >
                        Add Table
                      </Button>
                    )}
                    <Button onClick={refreshTables}>Refresh</Button>
                  </Space>
                  {tables.length > 0 ? (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(280px, 1fr))",
                        gap: "16px",
                      }}
                    >
                      {tables.map((table) => (
                        <Card
                          key={table.id}
                          title={`Table ${table.table_number}`}
                          extra={
                            <Tag
                              color={
                                table.status === "available"
                                  ? "green"
                                  : table.status === "occupied"
                                    ? "red"
                                    : "gold"
                              }
                            >
                              {table.status}
                            </Tag>
                          }
                          actions={
                            canManage
                              ? [
                                  <Button
                                    type="text"
                                    icon={<EditOutlined />}
                                    onClick={() => {
                                      setEditingTable(table);
                                      tableForm.setFieldsValue(table);
                                      setTableModalVisible(true);
                                    }}
                                  >
                                    Edit
                                  </Button>,
                                  <Popconfirm
                                    title="Delete this table?"
                                    onConfirm={() => deleteTable(table.id)}
                                  >
                                    <Button
                                      type="text"
                                      danger
                                      icon={<DeleteOutlined />}
                                    >
                                      Delete
                                    </Button>
                                  </Popconfirm>,
                                ]
                              : []
                          }
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                              color: "#555",
                            }}
                          >
                            <Space>
                              <TeamOutlined style={{ color: "#8c8c8c" }} />
                              <Text>{table.capacity} persons</Text>
                            </Space>
                            <Space>
                              <EnvironmentOutlined
                                style={{ color: "#8c8c8c" }}
                              />
                              <Text>{table.location || "N/A"}</Text>
                            </Space>
                          </div>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px 0",
                        color: "#999",
                      }}
                    >
                      <Text type="secondary">
                        No tables found. Add a table to get started.
                      </Text>
                    </div>
                  )}
                </div>
              ),
            },
            {
              key: "menu",
              label: "Menu",
              children: (
                <div>
                  <Space style={{ marginBottom: 16 }}>
                    {canManage && (
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                          setEditingCategory(null);
                          categoryForm.resetFields();
                          setCategoryModalVisible(true);
                        }}
                      >
                        Add Category
                      </Button>
                    )}
                    {canManage && (
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                          setEditingMenuItem(null);
                          setItemModalVisible(true);
                        }}
                      >
                        Add Item
                      </Button>
                    )}
                    <Button onClick={refreshMenu}>Refresh</Button>
                  </Space>
                  <Table
                    rowKey="id"
                    columns={categoryColumns}
                    dataSource={categories}
                    pagination={false}
                    style={{ marginBottom: 16 }}
                    scroll={{ x: "max-content" }}
                  />
                  {canManage && (
                    <div style={{ marginBottom: 24 }}>
                      <Typography.Title level={5} style={{ marginBottom: 12 }}>Kitchen Printer Groups</Typography.Title>
                      <KitchenGroupManagement visible={true} />
                    </div>
                  )}
                  <div style={{ marginTop: 24, marginBottom: 16 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 16,
                        flexWrap: "wrap",
                        gap: "16px",
                      }}
                    >
                      <h3 style={{ margin: 0 }}>
                        Menu Items ({filteredMenuItems.length})
                      </h3>
                      <Space wrap>
                        <Input.Search
                          placeholder="Search menu items..."
                          allowClear
                          onChange={(e) => setMenuSearchQuery(e.target.value)}
                          style={{ width: 250 }}
                        />
                        <Select
                          value={menuFilterCategory}
                          onChange={setMenuFilterCategory}
                          style={{ width: 180 }}
                          options={[
                            { label: "All Categories", value: "all" },
                            ...categories.map((c) => ({
                              label: c.name,
                              value: c.id,
                            })),
                          ]}
                        />
                        <Select
                          value={menuFilterAvailability}
                          onChange={setMenuFilterAvailability}
                          style={{ width: 150 }}
                          options={[
                            { label: "All Status", value: "all" },
                            { label: "Active", value: "active" },
                            { label: "Inactive", value: "inactive" },
                          ]}
                        />
                      </Space>
                    </div>
                    {filteredMenuItems.length > 0 ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "24px",
                        }}
                      >
                        {categories
                          .map((category) => ({
                            ...category,
                            items: filteredMenuItems.filter(
                              (item) => item.category_id === category.id,
                            ),
                          }))
                          .filter((category) => category.items.length > 0)
                          .map((category) => (
                            <div key={category.id}>
                              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                                <Typography.Title
                                  level={5}
                                  style={{ margin: 0, color: "#1890ff", marginRight: 16 }}
                                >
                                  {category.name} ({category.items.length})
                                </Typography.Title>
                                <div style={{ flex: 1, height: 1, background: '#e8e8e8' }} />
                              </div>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns:
                                    "repeat(auto-fill, minmax(280px, 1fr))",
                                  gap: "16px",
                                }}
                              >
                                {category.items.map((item) => (
                                  <MenuItemCard
                                    key={item.id}
                                    item={item}
                                    canManage={canManage}
                                    onEdit={(itemToEdit) => {
                                      setEditingMenuItem(itemToEdit);
                                      setItemModalVisible(true);
                                    }}
                                    onDelete={deleteMenuItem}
                                    onClick={setViewingMenuItem}
                                  />
                                ))}
                              </div>
                            </div>
                          ))}

                        {/* Uncategorized items if any */}
                        {filteredMenuItems.filter(
                          (item) =>
                            !categories.find((c) => c.id === item.category_id),
                        ).length > 0 && (
                          <div key="uncategorized">
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                              <Typography.Title
                                level={5}
                                style={{ margin: 0, color: "#1890ff", marginRight: 16 }}
                              >
                                Uncategorized (
                                {
                                  filteredMenuItems.filter(
                                    (item) =>
                                      !categories.find(
                                        (c) => c.id === item.category_id,
                                      ),
                                  ).length
                                }
                                )
                              </Typography.Title>
                              <div style={{ flex: 1, height: 1, background: '#e8e8e8' }} />
                            </div>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns:
                                  "repeat(auto-fill, minmax(280px, 1fr))",
                                gap: "16px",
                              }}
                            >
                              {filteredMenuItems
                                .filter(
                                  (item) =>
                                    !categories.find(
                                      (c) => c.id === item.category_id,
                                    ),
                                )
                                .map((item) => (
                                  <MenuItemCard
                                    key={item.id}
                                    item={item}
                                    canManage={canManage}
                                    onEdit={(itemToEdit) => {
                                      setEditingMenuItem(itemToEdit);
                                      setItemModalVisible(true);
                                    }}
                                    onDelete={deleteMenuItem}
                                    onClick={setViewingMenuItem}
                                  />
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "40px 0",
                          color: "#999",
                          backgroundColor: "#fafafa",
                          borderRadius: "8px",
                        }}
                      >
                        <Typography.Text type="secondary">
                          No menu items found. Add an item to get started or
                          adjust your filters.
                        </Typography.Text>
                      </div>
                    )}
                  </div>
                </div>
              ),
            },
            {
              key: "website-content",
              label: "Website Content",
              children: (
                <div>
                  <Space style={{ marginBottom: 16 }}>
                    {canManage && (
                      <Button
                        type="primary"
                        onClick={() => {
                          setContentModalVisible(true);
                        }}
                      >
                        Edit Website Content
                      </Button>
                    )}
                    <Button onClick={refreshWebsiteContent}>Refresh</Button>
                  </Space>

                  <Space
                    direction="vertical"
                    size="large"
                    style={{ width: "100%" }}
                  >
                    <Descriptions
                      title="Menu Hero"
                      bordered
                      column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
                      size="small"
                      style={{ backgroundColor: "#fff", borderRadius: 8 }}
                    >
                      <Descriptions.Item label="Eyebrow">
                        {websiteContent?.menu_hero_eyebrow || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Title">
                        {websiteContent?.menu_hero_title || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Subtitle" span={2}>
                        {websiteContent?.menu_hero_subtitle || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Image Preview" span={2}>
                        {websiteContent?.menu_hero_image ? (
                          <img
                            src={websiteContent.menu_hero_image}
                            alt="Menu Hero"
                            style={{
                              maxWidth: "100%",
                              maxHeight: 120,
                              objectFit: "cover",
                              borderRadius: 8,
                              border: "1px solid #f0f0f0",
                            }}
                          />
                        ) : (
                          "-"
                        )}
                      </Descriptions.Item>
                    </Descriptions>

                    <Descriptions
                      title="Restaurant Hero"
                      bordered
                      column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
                      size="small"
                      style={{ backgroundColor: "#fff", borderRadius: 8 }}
                    >
                      <Descriptions.Item label="Eyebrow">
                        {websiteContent?.restaurant_hero_eyebrow || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Title">
                        {websiteContent?.restaurant_hero_title || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Subtitle" span={2}>
                        {websiteContent?.restaurant_hero_subtitle || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Image Preview" span={2}>
                        {websiteContent?.restaurant_hero_image ? (
                          <img
                            src={websiteContent.restaurant_hero_image}
                            alt="Restaurant Hero"
                            style={{
                              maxWidth: "100%",
                              maxHeight: 120,
                              objectFit: "cover",
                              borderRadius: 8,
                              border: "1px solid #f0f0f0",
                            }}
                          />
                        ) : (
                          "-"
                        )}
                      </Descriptions.Item>
                    </Descriptions>

                    <Descriptions
                      title="Intro Section"
                      bordered
                      column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
                      size="small"
                      style={{ backgroundColor: "#fff", borderRadius: 8 }}
                    >
                      <Descriptions.Item label="Kicker">
                        {websiteContent?.restaurant_intro_kicker || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Title">
                        {websiteContent?.restaurant_intro_title || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Paragraph 1" span={2}>
                        {websiteContent?.restaurant_intro_paragraph_one || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Paragraph 2" span={2}>
                        {websiteContent?.restaurant_intro_paragraph_two || "-"}
                      </Descriptions.Item>
                    </Descriptions>

                    <Descriptions
                      title="Reservation Section"
                      bordered
                      column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
                      size="small"
                      style={{ backgroundColor: "#fff", borderRadius: 8 }}
                    >
                      <Descriptions.Item label="Kicker">
                        {websiteContent?.menu_reservation_kicker || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Title">
                        {websiteContent?.menu_reservation_title || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Description" span={2}>
                        {websiteContent?.menu_reservation_description || "-"}
                      </Descriptions.Item>
                    </Descriptions>

                    <Descriptions
                      title="Other Sections"
                      bordered
                      column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
                      size="small"
                      style={{ backgroundColor: "#fff", borderRadius: 8 }}
                    >
                      <Descriptions.Item label="Featured Kicker">
                        {websiteContent?.featured_section_kicker || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Featured Title">
                        {websiteContent?.featured_section_title || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Experiences Kicker">
                        {websiteContent?.experiences_section_kicker || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Experiences Title">
                        {websiteContent?.experiences_section_title || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Menu Feature Image" span={2}>
                        {websiteContent?.menu_feature_image ? (
                          <img
                            src={websiteContent.menu_feature_image}
                            alt="Menu Feature"
                            style={{
                              maxWidth: "100%",
                              maxHeight: 120,
                              objectFit: "cover",
                              borderRadius: 8,
                              border: "1px solid #f0f0f0",
                            }}
                          />
                        ) : (
                          "-"
                        )}
                      </Descriptions.Item>
                    </Descriptions>

                    <Descriptions
                      title="Call To Action"
                      bordered
                      column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
                      size="small"
                      style={{ backgroundColor: "#fff", borderRadius: 8 }}
                    >
                      <Descriptions.Item label="Title">
                        {websiteContent?.cta_title || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Button Text">
                        {websiteContent?.cta_button_text || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Description" span={2}>
                        {websiteContent?.cta_description || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Button Link" span={2}>
                        {websiteContent?.cta_button_link || "-"}
                      </Descriptions.Item>
                    </Descriptions>

                    <Card
                      title="Dynamic Content"
                      size="small"
                      style={{ borderRadius: 8 }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "24px",
                          flexWrap: "wrap",
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 300 }}>
                          <strong>Opening Hours</strong>
                          <ul style={{ paddingLeft: 20, marginTop: 8 }}>
                            {websiteContent?.opening_hours?.map((h, i) => (
                              <li key={i}>
                                {h.meal}: {h.hours}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div style={{ flex: 1, minWidth: 300 }}>
                          <strong>Dining Experiences</strong>
                          <ul style={{ paddingLeft: 20, marginTop: 8 }}>
                            {websiteContent?.dining_experiences?.map((d, i) => (
                              <li key={i}>
                                {d.icon} <strong>{d.name}</strong> -{" "}
                                {d.description}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </Card>
                  </Space>
                </div>
              ),
            },
            {
              key: "orders",
              label: "Orders & Billing",
              children: (
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: "16px" }}>
                    <Space wrap>
                      <Input.Search
                        placeholder="Search orders, guests, rooms..."
                        allowClear
                        value={orderSearchQuery}
                        onChange={(e) => setOrderSearchQuery(e.target.value)}
                        style={{ width: 250 }}
                      />
                      <DatePicker 
                        value={orderDate}
                        onChange={(date) => setOrderDate(date)}
                        disabledDate={(current) => current && current > dayjs().endOf('day')}
                        style={{ width: 150 }}
                        allowClear
                        placeholder="Select Date"
                      />
                      <Radio.Group 
                        value={orderFilterType} 
                        onChange={(e) => setOrderFilterType(e.target.value)}
                        buttonStyle="solid"
                      >
                        <Radio.Button value="all">All</Radio.Button>
                        <Radio.Button value="dine_in">Dine In</Radio.Button>
                        <Radio.Button value="room_service">Room Service</Radio.Button>
                        <Radio.Button value="take_away">Take Away</Radio.Button>
                      </Radio.Group>
                    </Space>
                    <Space wrap>
                      <Button onClick={refreshOrders}>Refresh</Button>
                      {canTakeOrders && (
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={() => {
                            setEditingOrder(null);
                            refreshTables();
                            setOrderModalVisible(true);
                          }}
                        >
                          Create Order
                        </Button>
                      )}
                    </Space>
                  </div>
                  <style>
                    {`
                      .ongoing-order-row > td {
                        background-color: #f6ffed !important;
                      }
                      .ongoing-order-row:hover > td {
                        background-color: #e5f6d8 !important;
                      }
                      .inactive-order-row > td {
                        opacity: 0.6;
                        background-color: #fafafa !important;
                      }
                      .inactive-order-row:hover > td {
                        opacity: 0.8;
                        background-color: #f0f0f0 !important;
                      }
                      @keyframes pulse-dot {
                        0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.7); }
                        70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(82, 196, 26, 0); }
                        100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(82, 196, 26, 0); }
                      }
                      .pulse-indicator {
                        display: inline-block;
                        width: 8px;
                        height: 8px;
                        border-radius: 50%;
                        background: #52c41a;
                        margin-left: 8px;
                        animation: pulse-dot 2s infinite;
                      }
                    `}
                  </style>
                  <Table
                    rowKey="id"
                    columns={orderColumns}
                    dataSource={filteredOrders}
                    pagination={{ pageSize: 15 }}
                    scroll={{ x: "max-content" }}
                    rowClassName={(record: any) => {
                      if (['paid', 'cancelled'].includes(record.status)) {
                        return "inactive-order-row";
                      }
                      const isRoomService = record.order_type === "room_service";
                      const maxDays = Number(record.max_days) || 1;
                      if (!isRoomService || maxDays <= 1) return "";
                      
                      const startDate = new Date(record.created_at);
                      const endDate = new Date(startDate);
                      endDate.setDate(endDate.getDate() + maxDays - 1);
                      const now = new Date();
                      const startOfDay = new Date(startDate);
                      startOfDay.setHours(0, 0, 0, 0);
                      const endOfDay = new Date(endDate);
                      endOfDay.setHours(23, 59, 59, 999);
                      
                      return (now >= startOfDay && now <= endOfDay) ? "ongoing-order-row" : "";
                    }}
                    onRow={(record: any) => ({
                      onClick: () => {
                        setSelectedOrderId(record.id);
                        setOrderDetailsVisible(true);
                      },
                      style: { cursor: "pointer" }
                    })}
                  />
                </div>
              ),
            },
            {
              key: "kot",
              label: "KOT Queue",
              children: (
                <div>
                  <Space style={{ marginBottom: 16 }}>
                    <Button onClick={refreshKot}>Refresh KOT</Button>
                    <Text type="secondary">
                      {kotOrderGroups.length} order{kotOrderGroups.length === 1 ? "" : "s"} in queue — newest first
                    </Text>
                  </Space>
                  <Table
                    rowKey="order_id"
                    columns={kotOrderColumns}
                    dataSource={kotOrderGroups}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: "max-content" }}
                    expandable={{
                      expandedRowRender: (record: KotOrderGroup) => (
                        <Table
                          rowKey="order_item_id"
                          size="small"
                          columns={kotItemColumns}
                          dataSource={record.items}
                          pagination={false}
                          scroll={{ x: "max-content" }}
                        />
                      ),
                    }}
                  />
                </div>
              ),
            },
          ]}
        />
      </Spin>

      <MenuItemDetailsDialog
        item={viewingMenuItem}
        visible={!!viewingMenuItem}
        onClose={() => setViewingMenuItem(null)}
        canManage={canManage}
        onEdit={(itemToEdit) => {
          setEditingMenuItem(itemToEdit);
          setItemModalVisible(true);
        }}
        onDelete={deleteMenuItem}
      />

      <Modal
        title={editingTable ? "Edit Dining Table" : "Add Dining Table"}
        open={tableModalVisible}
        onCancel={() => {
          setTableModalVisible(false);
          setEditingTable(null);
        }}
        onOk={() => tableForm.submit()}
      >
        <Form form={tableForm} layout="vertical" onFinish={createTable}>
          <Form.Item
            name="table_number"
            label="Table Number"
            rules={[{ required: true, message: "Table number is required" }]}
          >
            <Input placeholder="e.g. T1" />
          </Form.Item>
          <Form.Item
            name="capacity"
            label="Capacity"
            rules={[{ required: true, message: "Capacity is required" }]}
          >
            <InputNumber min={1} max={20} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="location" label="Location">
            <Input placeholder="e.g. Pool side" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingCategory ? "Edit Menu Category" : "Add Menu Category"}
        open={categoryModalVisible}
        onCancel={() => {
          setCategoryModalVisible(false);
          setEditingCategory(null);
        }}
        onOk={() => categoryForm.submit()}
      >
        <Form form={categoryForm} layout="vertical" onFinish={createCategory}>
          <Form.Item
            name="name"
            label="Category Name"
            rules={[{ required: true, message: "Category name is required" }]}
          >
            <Input placeholder="e.g. Main Course" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Optional description" />
          </Form.Item>
          <Form.Item
            name="display_order"
            label="Display Order"
            initialValue={0}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" initialValue={true}>
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              style={{ width: "100%", display: "flex" }}
            >
              <Radio.Button
                value={true}
                style={{ flex: 1, textAlign: "center" }}
              >
                Yes
              </Radio.Button>
              <Radio.Button
                value={false}
                style={{ flex: 1, textAlign: "center" }}
              >
                No
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>

      <MenuItemFormModal
        visible={itemModalVisible}
        onCancel={() => {
          setItemModalVisible(false);
          setEditingMenuItem(null);
        }}
        onSubmit={createMenuItem}
        editingItem={editingMenuItem}
        categories={categories}
        kitchenGroups={kitchenGroups}
        loading={loading}
      />

      <CreatePosOrderForm
        visible={orderModalVisible}
        onCancel={() => {
          setOrderModalVisible(false);
          setEditingOrder(null);
        }}
        onSubmit={createOrder}
        tables={tables}
        menuItems={menuItems}
        categories={categories}
        checkedInBookings={checkedInBookings}
        initialData={editingOrder}
      />

      <EditWebsiteContentForm
        visible={contentModalVisible}
        onCancel={() => setContentModalVisible(false)}
        onSubmit={saveWebsiteContent}
        initialValues={websiteContent}
        loading={loading}
      />

      <Modal
        title="Confirm Payment"
        open={!!paidStatusOrderId}
        onCancel={() => setPaidStatusOrderId(null)}
        onOk={confirmPaidStatus}
        okText="Confirm Payment"
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text>Payment method</Text>
          <Select
            value={paidStatusMethod}
            onChange={setPaidStatusMethod}
            options={PAYMENT_METHOD_OPTIONS}
            style={{ width: "100%" }}
          />
        </Space>
      </Modal>

      <OrderDetailsModal
        visible={orderDetailsVisible}
        orderId={selectedOrderId}
        onCancel={() => setOrderDetailsVisible(false)}
        onUpdate={() => {
          refreshOrders();
          refreshKot();
        }}
        onPrintReceipt={printReceipt}
        onPrintKot={printKot}
        onPrintTaxInvoice={openTaxInvoiceModal}
        onPrintA4={(orderId) => openReceiptPdf(orderId)}
        onEditOrder={(data) => {
          setEditingOrder(data);
          setOrderModalVisible(true);
        }}
      />

      <TaxInvoiceModal
        visible={taxInvoiceVisible}
        orderId={taxInvoiceOrderId}
        bookingId={taxInvoiceBookingId}
        guestName={taxInvoiceGuestName}
        onCancel={() => setTaxInvoiceVisible(false)}
        onPrint={printTaxInvoice}
      />
    </div>
  );
};

export default RestaurantPOS;
